import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { MatCheckboxModule } from "@angular/material/checkbox";
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { ColDef } from "ag-grid-community";
import { cloneDeep, flatten } from "lodash";
import { Subject } from "rxjs";
import { LevelModalComponent } from "../../../features/corporation-management/level-modal/level-modal.component";
import { AppConstants } from "../../constants/app-constants";
import { ClientSideListBaseComponent } from "../../core/client-side-list-base/client-side-list-base.component";
import { LevelMode } from "../../enums/level-mode.enum";
import { ButtonComponent } from "../core/button/button.component";
import { rowDragText } from "./hierarchy.config";
import {
  Hierarchy,
  HierarchyDataItem,
  HierarchyLevel,
} from "./hierarchy.interface";
import { MatTooltipModule } from "@angular/material/tooltip";

@Component({
  selector: "thera-hierarchy",
  standalone: true,
  styleUrls: ["./hierarchy.component.scss"],
  templateUrl: "./hierarchy.component.html",
  imports: [
    MatExpansionModule,
    CommonModule,
    ClientSideListBaseComponent,
    MatCheckboxModule,
    ButtonComponent,
    MatDialogModule,
    MatTooltipModule,
  ],
})
export class HierarchyComponent implements OnInit {
  unmappedChecked = false;
  _hierarchyData!: Hierarchy;
  rowDragText = rowDragText;
  @Input() emitOnChange = false;
  @Input() tableSelect: boolean = false;
  @Input() addLevel$!: Subject<any>;
  @Input() corpId!: string;
  @Input()
  set hierarchyData(data: Hierarchy) {
    this._hierarchyData = cloneDeep(data);
  }

  get hierarchyData(): Hierarchy {
    return this._hierarchyData;
  }
  @Input() draggable: boolean = false;
  @Input() selectable: boolean = true;
  @Input() showAddLocationsButton = false;
  @Input() rowSelection: "single" | "multiple" | undefined;

  @Input() columnDefs!: ColDef[];
  @Output() facilitiesSelected: EventEmitter<string[]> = new EventEmitter();
  includedFacilities: any[] = [];
  @Output() hierarchyUpdated = new EventEmitter();
  @Output() facilityMoved = new EventEmitter();

  constructor(private dialog: MatDialog) {}

  ngOnInit(): void {
    if (this.addLevel$) {
      this.addLevel$.subscribe(() => this.onAddLevel(null));
    }
  }

  onCheck(item: any, newCheckedValue: boolean) {
    this.walkAndCheck(item, newCheckedValue);
  }

  onAddLocations() {
    this.facilitiesSelected.emit(this.includedFacilities);
    const filterCheckedItems = (hierarchy: HierarchyLevel) => {
      if (hierarchy.items?.length) {
        hierarchy.items = hierarchy.items.filter((item) => !item.checked);
      }

      if (hierarchy.levels?.length) {
        hierarchy.levels.forEach((level) => filterCheckedItems(level));
      }
    };

    const processHierarchies = (hierarchyData: Hierarchy) => {
      hierarchyData.hierarchies.forEach((hierarchy) => {
        filterCheckedItems(hierarchy);
      });
    };

    processHierarchies(this.hierarchyData);

    this.includedFacilities = [];
  }

  private walkAndCheck(
    item: HierarchyLevel & HierarchyDataItem,
    newCheckedValue: boolean
  ) {
    try {
      item.checked = newCheckedValue;
      if (item.facilityname) {
        if (this.includedFacilities.indexOf(item) === -1) {
          this.includedFacilities.push(item);
        } else {
          this.includedFacilities = this.includedFacilities.filter(
            (x) => x.id !== item.id
          );
        }
      }

      if (item.items) {
        item.items.forEach((facility) => {
          if (
            facility.status.toLocaleLowerCase() !==
            AppConstants.disabled.toLocaleLowerCase()
          )
            return this.walkAndCheck(facility as any, newCheckedValue);
        });

        item.items = [...item.items];
      }

      if (item.levels) {
        item.levels.forEach((facility) =>
          this.walkAndCheck(facility as any, newCheckedValue)
        );
      }

      if (newCheckedValue === false) {
        this.hierarchyData.hierarchies.forEach((each) => {
          this.unselectParents(item.name, each);
        });
      }
    } catch (error) {
      console.log("unhandled error:", error);
    }

    if (this.emitOnChange) {
      this.facilitiesSelected.emit(this.getAllFacilities());
    }
  }

  checkUnmappedFacilities() {
    this.unmappedChecked = !this.unmappedChecked;

    if (!this.unmappedChecked) {
      this.hierarchyData.unmappeditems.forEach(
        (item) => (item.checked = false)
      );
    } else {
      this.hierarchyData.unmappeditems.forEach((item) => {
        if (
          item.status.toLocaleLowerCase() !==
          AppConstants.disabled.toLocaleLowerCase()
        ) {
          item.checked = true;
          this.includedFacilities.push(item);
        }
      });
    }
  }

  unselectParents(name: string, searchPivot: any): boolean {
    if (searchPivot.name === name) return true;
    else if (searchPivot.levels?.length !== 0) {
      for (let i = 0; i < searchPivot?.levels?.length; i++) {
        let value = this.unselectParents(name, searchPivot.levels[i]);
        if (value === true) {
          searchPivot.checked = false;
          return true;
        }
      }
    } else if (searchPivot.items?.length !== 0) {
      for (let i = 0; i < searchPivot?.items?.length; i++) {
        let value = this.unselectParents(name, searchPivot.items[i]);
        if (value === true) {
          searchPivot.checked = false;
          return true;
        }
      }
    }
    return false;
  }

  onAddLevel(parentLevel: HierarchyLevel | null) {
    this.handleClose(
      this.dialog.open(LevelModalComponent, {
        data: {
          mode: LevelMode.ADD,
          parentLevel,
          corpId: this.corpId,
        },
      })
    );
  }

  async onEditLevel(levelToEdit: HierarchyLevel) {
    this.handleClose(
      this.dialog.open(LevelModalComponent, {
        data: {
          mode: LevelMode.EDIT,
          currentLevel: levelToEdit,
          corpId: this.corpId,
        },
      })
    );
  }

  async onDeleteLevel(levelToDelete: HierarchyLevel) {
    this.handleClose(
      this.dialog.open(LevelModalComponent, {
        data: {
          mode: LevelMode.DELETE,
          currentLevel: levelToDelete,
          corpId: this.corpId,
          facilitiesToReAssign: this.getAllFacilitiesUnderLevel(levelToDelete),
          reassignableLevels: this.getAllLevelsWithFacilities().filter(
            (x) => x.id !== levelToDelete.id
          ),
        },
      })
    );
  }

  private handleClose(dialogRef: MatDialogRef<LevelModalComponent>) {
    dialogRef.afterClosed().subscribe(() => {
      this.hierarchyUpdated.emit();
    });
  }

  private getAllFacilitiesUnderLevel(
    level: HierarchyLevel
  ): HierarchyDataItem[] {
    let levels = [] as HierarchyDataItem[];
    if (level.levels) {
      levels.push(
        ...flatten(
          level.levels.map((subLevel) =>
            this.getAllFacilitiesUnderLevel(subLevel)
          )
        )
      );
    }

    if (level.items) {
      levels.push(...level.items);
    }

    return levels;
  }

  onRowAdded(event: any, levelToRemove: HierarchyLevel | null) {
    const levelToAdd: HierarchyLevel | null = this.findLevelById(
      +event.node.id,
      this._hierarchyData.hierarchies
    );
    if (levelToAdd) {
      levelToAdd.items = [...levelToAdd.items, event.data];
      this.facilityMoved.emit({
        facilityId: event.data.id,
        levelId: levelToAdd.id,
      });
    }

    if (levelToRemove) {
      levelToRemove.items = levelToRemove.items.filter(
        (item) => item.id !== event.data.id
      );
    } else {
      this.hierarchyData.unmappeditems =
        this.hierarchyData.unmappeditems.filter(
          (item) => item.id !== event.data.id
        );
    }
  }

  getAllFacilities() {
    return flatten(
      this.hierarchyData.hierarchies.map((level) =>
        this.getAllFacilitiesRecursive(level)
      )
    );
  }

  getAllFacilitiesRecursive(level: HierarchyLevel): any {
    let levels = [] as any;
    if (level.levels?.length) {
      levels.push(
        ...flatten(
          level.levels.map((subLevel) =>
            this.getAllFacilitiesRecursive(subLevel)
          )
        )
      );
    }

    if (level.items?.length) {
      levels.push(...level.items);
    }

    return levels;
  }

  private findLevelById(
    id: number,
    levels: HierarchyLevel[]
  ): HierarchyLevel | null {
    let foundLevel: HierarchyLevel | null = null;
    for (let i = 0; i < levels.length; i++) {
      if (levels[i].id === id) {
        foundLevel = levels[i];
        break;
      } else {
        foundLevel = this.findLevelById(id, levels[i].levels);
        if (foundLevel) {
          break;
        }
      }
    }

    return foundLevel;
  }

  getAllLevelsWithFacilities() {
    return flatten(
      this.hierarchyData.hierarchies.map((level) =>
        this.getAllLevelsWithFacilitiesRecursive(level)
      )
    );
  }
  getAllLevelsWithFacilitiesRecursive(level: HierarchyLevel): any {
    let levels = [] as any;
    if (level.levels?.length) {
      levels.push(
        ...flatten(
          level.levels.map((subLevel) =>
            this.getAllLevelsWithFacilitiesRecursive(subLevel)
          )
        )
      );
    }

    if (level.canmovein) {
      levels.push(level);
    }

    return levels;
  }

  levelTrackBy(index: number, level: HierarchyLevel) {
    return level.id;
  }

  itemTrackBy(index: number, item: HierarchyDataItem) {
    return item.id;
  }
}
